/**
 * @module app.pp
 */

import { autoinject } from 'aurelia-framework';
import { ComponentBase, Sorting, SortDirection } from 'app/shared/lib/essette-site';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { TaskService } from 'app/core/services/task-service';
import { apiCall } from 'app/shared/lib/essette-api';
import { ITasksConfig } from 'app/core/models/essette-interfaces';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { EssTableCustomElement } from 'app/shared/elements/ess-table';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { openCaseSummary } from 'app/cm/components/summary/case-summary';
import { EventAggregator } from 'aurelia-event-aggregator';
import { ReturnFocusEvent, StoreFocusEvent } from 'app/shared/lib/events';
import * as CM from 'app/cm/models/essette-api';
import { requiredFields } from 'app/cd/elements/members/member-display';

@autoinject()
export class TodoGrid extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    protected taskResults: ReadonlyArray<CM.Task> = [];

    protected loadModal: boolean = false;

    private sorting: Sorting = new Sorting();
    protected resultCount: number = 0;
    protected pageSize: number = 20;

    private hasTaskRoute: boolean = false;
    private hasInterventionRoute: boolean = false;

    protected showCaseSummaryLink: boolean = false;
    protected openCaseSummary = openCaseSummary;


    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;
    protected $table!: EssTableCustomElement<CM.Task>;


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly taskService: TaskService,
        private readonly events: EventAggregator,
        ...rest: any[]) {

        super(...rest);

        const tasksConfig: ITasksConfig = {
            serviceRootUrl: 'cm'
        };
        this.taskService.configure(tasksConfig);
    }

    protected bind(bindingContext: Object, overrideContext: Object): void {
        this.checkPermissions();

        this.reload();
    }


    ////****************** Events ***************************************************////
    private async reload(): Promise<void> {
        await this.loadTasks();
        this.events.publish(new ReturnFocusEvent());
    }

    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const { sorting } = this;

        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        this.reload();
    }

    protected handleLinkClickCapture(): boolean {
        this.events.publish(new StoreFocusEvent());
        return true;
    }

    ////****************** Functions ************************************************////
    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        this.hasTaskRoute = this.accountContext.hasRouteAccess('pp.task');
        this.hasInterventionRoute = this.accountContext.hasRouteAccess('pp.intervention');
        this.showCaseSummaryLink = this.accountContext.hasRouteAccess('cm.case.summary');
    }

    public clear(): void {
        this.taskResults = [];
        if(this.$pager) this.$pager.updatePage(0);
        this.resultCount = 0;
    }

    protected hasRouteAccess(task: CM.Task): boolean {
        return this.isIntervention(task) ? this.hasInterventionRoute : this.hasTaskRoute;
    }

    protected buildRoute(task: CM.Task): string {
        if(!task.case || !task.case.id) { throw new Error('Could not find case information'); }

        const typeCode = task.attachedTo?.type?.code;
        if(!typeCode) { throw new Error('Could not load task type information'); }

        switch(typeCode) {
            case 'CM.Case':
                return this.router.generate('pp.task', { taskId: task.id });
            case 'CM.CarePlanGoal':
                return this.router.generate('pp.intervention', { interventionId: task.id });
            default:
                throw new Error(`Unexpected typeCode: ${typeCode}`);
        }
    }

    private isIntervention(task: CM.Task): boolean {
        return task.attachedTo?.type?.code === 'CM.CarePlanGoal';
    }

    protected getTaskLabel(task: CM.Task): string {
        return this.isIntervention(task) ? 'cm:carePlanIntervention.$' : 'core:task.$';
    }

    protected getTaskIcon(task: CM.Task): string {
        return `ui:icons.${this.isIntervention(task) ? 'carePlan' : 'task'}`;
    }


    ////****************** Service Calls ********************************************////
    @apiCall
    public async loadTasks(): Promise<void> {
        const options: Partial<IQueryLoadOptions> = {
            sorting: this.sorting,
            top: this.pageSize,
            page: this.$pager?.page || 0,
            includeInactive: false
        };

        const includes: string[] = ['member', 'case'];
        const fields: string[] = [
            'attachedTo.type.code',
            'case.number',
            'description',
            'due.date',
            'isCompleted',
            'member.eligibility.isCurrent'
        ];
        requiredFields.forEach(field => !fields.includes(`member.${field}`) ? fields.push(`member.${field}`) : null);

        try {
            this.state.beginLoad();
            const response = await this.taskService.load(options, includes, fields);
            this.taskResults = response?.results || [];
            this.resultCount = response?.count || 0;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

}
